import { useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Combobox from '../base/Combobox';
import { formatAmount } from '../../utils/numberFormat';
import { toLatinDigits } from '../../utils/arabicNormalize';
import { MAX_AMOUNT_DIGITS, countDigits, cx, indexAfterDigits, toDigitString, useNsText } from './searchUi';

/**
 * A number field with preset suggestions: pick a value from the list that opens
 * BELOW it, or type any number. Used for the price and area cells of
 * RangeFilterGrid. See Apps/web/THEME.md.
 *
 * - Stores a plain digit string ('' = no bound). Arabic-Indic digits are read as
 *   Latin, separators and letters are dropped, leading zeros are trimmed.
 * - Shows the value with Western digits and "," grouping (formatAmount), live while
 *   typing, with the caret kept after the same digit.
 * - Typing narrows the list to the presets that start with the typed digits. A
 *   number that matches no preset is still the value; the list simply closes.
 * - Built on Combobox, so the ARIA (role="combobox", aria-activedescendant), the
 *   keyboard and the downward-opening popover are the shared ones.
 */

export interface PresetNumberFieldProps {
  id?: string;
  /** Accessible name, e.g. «السعر (سنوياً): من». Visible above the field unless `hideLabel`. */
  label: string;
  hideLabel?: boolean;
  /** Digit string; '' = no value. */
  value: string;
  onChange: (value: string) => void;
  /** Suggested values, ascending. Empty = typing only. */
  presets: readonly number[];
  /** Shown while empty. Default «الكل». */
  placeholder?: string;
  /** A short word inside the field at the inline start, e.g. «من». Decorative: put it in `label` too. */
  prefix?: string;
  invalid?: boolean;
  'aria-describedby'?: string;
  /** Read to screen readers after the name, e.g. «أو اكتب مبلغاً». */
  typingHint?: string;
  /** 'field': bordered form field. 'bare': borderless, fills a cell of a larger control. */
  variant?: 'field' | 'bare';
  maxDigits?: number;
  disabled?: boolean;
  /** Lower bound on the list's width, so a narrow cell still gets a readable list. Default '9rem'. */
  popoverMinWidth?: number | string;
  /**
   * The last preset is open-ended and reads «N+» («500,000+») in the list and in the
   * field. What it means (N or more / no upper limit) is the caller's business.
   */
  openEndedTop?: boolean;
  className?: string;
}

export default function PresetNumberField({
  id: idProp,
  label,
  hideLabel,
  value,
  onChange,
  presets,
  placeholder,
  prefix,
  invalid = false,
  'aria-describedby': ariaDescribedBy,
  typingHint,
  variant = 'field',
  maxDigits = MAX_AMOUNT_DIGITS,
  disabled = false,
  popoverMinWidth = '9rem',
  openEndedTop = false,
  className = '',
}: PresetNumberFieldProps) {
  const { text } = useNsText('ui');
  const generatedId = useId();
  const inputId = idProp ?? `preset-number-${generatedId}`;
  const hintId = `${inputId}-hint`;

  const inputRef = useRef<HTMLInputElement | null>(null);
  // Digits that should sit before the caret once the reformatted value renders.
  const caretRef = useRef<number | null>(null);
  // True between a keystroke and the next pick / blur: the list narrows to the typed digits.
  const [editing, setEditing] = useState(false);
  // Forces a render when a keystroke changes nothing (a letter), so the caret is put back.
  const [, setRenderTick] = useState(0);

  const digits = toDigitString(value, maxDigits);
  const top = openEndedTop && presets.length ? presets[presets.length - 1] : null;
  const optionLabel = (n: number) => (top !== null && n === top ? `${formatAmount(n)}+` : formatAmount(n));
  // The «N+» mark shows while the field is not being typed into.
  const display = digits
    ? !editing && top !== null && digits === String(top)
      ? `${formatAmount(digits)}+`
      : formatAmount(digits)
    : '';

  const options = useMemo(
    () => (editing && digits ? presets.filter((n) => String(n).startsWith(digits)) : presets),
    [editing, digits, presets],
  );
  const selectedKey = digits && presets.some((n) => String(n) === digits) ? digits : null;

  const handleInput = (raw: string) => {
    const el = inputRef.current;
    const latin = toLatinDigits(raw);
    // Same length as `raw` (each Arabic-Indic digit maps to one Latin digit).
    const caret = el && el.value === raw && typeof el.selectionStart === 'number' ? el.selectionStart : latin.length;
    const typed = latin.replace(/\D/g, '');
    const leadingZeros = typed.length - typed.replace(/^0+(?=\d)/, '').length;
    const next = toDigitString(latin, maxDigits);
    caretRef.current = Math.min(next.length, Math.max(0, countDigits(latin.slice(0, caret)) - leadingZeros));
    setEditing(true);
    setRenderTick((n) => n + 1);
    if (next !== value) onChange(next);
  };

  const choose = (preset: number) => {
    setEditing(false);
    const next = String(preset);
    if (next !== value) onChange(next);
  };

  // Put the caret back after the same digit once the grouped value is on screen
  // (a controlled value change would otherwise throw it to the end).
  useLayoutEffect(() => {
    const target = caretRef.current;
    const el = inputRef.current;
    if (target === null || !el) return;
    caretRef.current = null;
    if (el.ownerDocument.activeElement !== el) return;
    const position = indexAfterDigits(el.value, target);
    try {
      el.setSelectionRange(position, position);
    } catch {
      // Input types without a selection API; nothing to restore.
    }
  });

  const describedBy = [ariaDescribedBy, typingHint ? hintId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cx('relative min-w-0', className)}>
      <Combobox<number>
        id={inputId}
        label={label}
        hideLabel={hideLabel}
        inputValue={display}
        onInputChange={handleInput}
        options={options}
        getOptionKey={(n) => String(n)}
        renderOption={(n) => <span className="tabular-nums">{optionLabel(n)}</span>}
        onSelect={choose}
        selectedKey={selectedKey}
        emptyText={null}
        placeholder={placeholder ?? text('range.any', 'Any')}
        leadingIcon={
          prefix ? (
            <span className="text-[0.875rem] leading-none text-[var(--pv-color-text-muted)]">{prefix}</span>
          ) : undefined
        }
        variant={variant}
        openOnFocus={presets.length > 0}
        disabled={disabled}
        invalid={invalid}
        popoverMinWidth={popoverMinWidth}
        inputRef={inputRef}
        inputProps={{
          inputMode: 'numeric',
          spellCheck: false,
          'aria-describedby': describedBy,
          onBlur: () => setEditing(false),
        }}
      />
      {typingHint && (
        <span id={hintId} className="sr-only">
          {typingHint}
        </span>
      )}
    </div>
  );
}
